import { isNil } from "lodash";
import { calculateTimeDifferenceInMinutes, convertMinutesToHoursAndMinutes } from "./date";
import * as dayjs from "dayjs";
import * as timezone from "dayjs/plugin/timezone";
import * as utc from "dayjs/plugin/utc";

dayjs.extend(timezone);
dayjs.extend(utc);

/**
 * Given an arrival date for the stop and a completed date for the stop, calculates the wait time in minutes.
 * The wait time is the difference between the completed date and the arrival date, rounded down to the nearest minute.
 * Returns null if input is invalid.
 */
export const calculateWaitTimeInMinutes = ({
  arrivalDate,
  completedDate,
}: {
  arrivalDate: Date | null | undefined;
  completedDate: Date | null | undefined;
}): {
  totalMinutes: number | null;
  hours: number | null;
  minutes: number | null;
} => {
  if (isNil(arrivalDate) || isNil(completedDate)) {
    return {
      totalMinutes: null,
      hours: null,
      minutes: null,
    };
  }

  const totalMinutes = calculateTimeDifferenceInMinutes({ startDate: arrivalDate, endDate: completedDate });

  if (isNil(totalMinutes)) {
    return {
      totalMinutes: null,
      hours: null,
      minutes: null,
    };
  }

  const { hours, minutes } = convertMinutesToHoursAndMinutes({ totalMinutes });
  return { totalMinutes, hours, minutes };
};
/**
 * Fallbacks for a company without a configuration row. They mirror the
 * database defaults of `defaultGenerateAppointmentEarlyCushionInMinutes` and
 * `defaultGenerateAppointmentDurationInMinutes`.
 */
export const DEFAULT_GENERATED_APPOINTMENT_EARLY_CUSHION_MINUTES = 90;
export const DEFAULT_GENERATED_APPOINTMENT_LATE_CUSHION_MINUTES = 180;

/** Upper bound, in minutes, for either appointment cushion a company can set. */
export const GENERATED_APPOINTMENT_CUSHION_MAX_MINUTES = 1440;

const GENERATED_APPOINTMENT_ROUNDING_MINUTES = 30;

/**
 * Derives an appointment window from a stop's ETA. The ETA is rounded down to
 * the half hour; the window opens `earlyCushionInMinutes` before that and
 * closes `lateCushionInMinutes` after it, so a driver running early is covered
 * as well as one running late. The start is not clamped, so an ETA shortly
 * after midnight yields a start on the previous calendar day.
 */
export const generateAppointmentWindowFromEta = ({
  etaArrivalTime,
  earlyCushionInMinutes,
  lateCushionInMinutes,
}: {
  etaArrivalTime: Date | string;
  earlyCushionInMinutes: number;
  lateCushionInMinutes: number;
}): { appointmentTime: Date; endAppointmentTime: Date } => {
  const eta = dayjs(etaArrivalTime);
  const roundedEta = eta.set(
    "minute",
    Math.floor(eta.minute() / GENERATED_APPOINTMENT_ROUNDING_MINUTES) * GENERATED_APPOINTMENT_ROUNDING_MINUTES,
  );
  return {
    appointmentTime: roundedEta.subtract(earlyCushionInMinutes, "minute").toDate(),
    endAppointmentTime: roundedEta.add(lateCushionInMinutes, "minute").toDate(),
  };
};
